import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { AlertCircle, Copy, RefreshCw, X } from 'lucide-react';
import './ErrorDisplay.css';

/**
 * ErrorDisplay Component
 * Displays user-friendly error messages with support tools and retry functionality
 */
const ErrorDisplay = ({ 
  error, 
  errorCategory, 
  correlationId, 
  retryable = true,
  onRetry,
  onDismiss,
  fileName,
  uploadedOn
}) => {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const isRTL = i18n.language === 'he';

  const copyErrorId = async () => {
    if (correlationId) {
      try {
        await navigator.clipboard.writeText(correlationId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy error ID:', err);
      }
    }
  };

  // Get user-friendly error message based on category
  const getErrorMessage = () => {
    if (error) {
      // If error is already a user-friendly message, use it
      if (typeof error === 'string' && !error.includes('Load failed')) {
        return error;
      }
    }

    // Map error categories to user-friendly messages
    const errorMessages = {
      V_TIMEOUT: isRTL 
        ? 'נראה שהניתוח לוקח יותר מדי זמן. נסה עם סרטון קצר יותר או נסה שוב מאוחר יותר.'
        : 'The video analysis took too long. Please try with a shorter video or try again later.',
      V_SIZE: isRTL
        ? 'קובץ הסרטון גדול מדי. אנא דחוס את הסרטון או הקלט קליפ קצר יותר. הגודל המקסימלי הוא 500MB.'
        : 'The video file is too large. Please compress the video or record a shorter clip. Maximum size is 500MB.',
      GEMINI_FILE_FAILED: isRTL
        ? 'לא הצלחנו לעבד את הסרטון הזה בגלל בעיה בפורמט או בקובץ עצמו. זה קורה לפעמים עם קבצים מסוימים מהמצלמה. נסה לייצא את הסרטון כ‑MP4 (H.264, 720p או 1080p) ולהעלות שוב.'
        : 'We couldn\'t process this video due to a technical issue with the file format. This can happen with certain camera recordings. Please export your video as MP4 (H.264, 720p or 1080p) and upload it again.',
      GEMINI_INTERNAL_ERROR: isRTL
        ? 'הייתה תקלה פנימית בשירות הניתוח שלנו בזמן עיבוד הסרטון. זו בעיה בצד שלנו, לא אצלך. אנא נסה שוב בעוד כמה דקות. אם זה ממשיך, מומלץ לייצא את הסרטון כ‑MP4 (H.264) ולהעלות שוב.'
        : 'There was an internal error in our analysis engine while processing your video. This is on our side, not yours. Please try again in a few minutes. If it keeps happening, try exporting your video as MP4 (H.264) and uploading again.',
      GEMINI_REJECTION: isRTL
        ? 'תוכן הסרטון נדחה על ידי מסנני האבטחה שלנו. אנא ודא שהסרטון מכיל תוכן מתאים לניתוח.'
        : 'The video content was rejected by our safety filters. Please ensure the video contains appropriate content for analysis.',
      GEMINI_RATE_LIMIT: isRTL
        ? 'שירות הניתוח שלנו עסוק כרגע. אנא נסה שוב בעוד כמה דקות.'
        : 'Our analysis service is currently busy. Please try again in a few minutes.',
      GEMINI_API_ERROR: isRTL
        ? 'הייתה בעיה בחיבור לשירות הניתוח שלנו. אנא נסה שוב בעוד רגע.'
        : 'There was an issue connecting to our analysis service. Please try again in a moment.',
      NETWORK_ERROR: isRTL
        ? 'לא ניתן להתחבר לשרת. אנא בדוק את חיבור האינטרנט שלך ונסה שוב.'
        : 'Unable to connect to the server. Please check your internet connection and try again.',
      VALIDATION_ERROR: isRTL
        ? 'פורמט קובץ הסרטון אינו נתמך. אנא השתמש בפורמט MP4, MOV או AVI.'
        : 'The video file format is not supported. Please use MP4, MOV, or AVI format.',
      STORAGE_ERROR: isRTL
        ? 'הייתה בעיה בשמירת הסרטון שלך. אנא נסה שוב.'
        : 'There was an issue saving your video. Please try again.',
      DATABASE_ERROR: isRTL
        ? 'הייתה בעיה בשמירת הניתוח שלך. הניתוח הושלם אך ייתכן שלא נשמר. אנא נסה שוב.'
        : 'There was an issue saving your analysis. The analysis completed but may not be saved. Please try again.',
      UNKNOWN_ERROR: isRTL
        ? 'אירעה שגיאה בלתי צפויה בעת עיבוד הסרטון שלך. הצוות שלנו קיבל הודעה.'
        : 'An unexpected error occurred while processing your video. Our team has been notified.'
    };

    return errorMessages[errorCategory] || errorMessages.UNKNOWN_ERROR;
  };

  const errorMessage = getErrorMessage();

  return (
    <div className={`error-display ${isRTL ? 'error-display--rtl' : ''}`}>
      <div className="error-display__card">
        <div className="error-display__header">
          <div className="error-display__icon-wrapper">
            <AlertCircle className="error-display__icon" size={24} />
          </div>
          <h3 className="error-display__title">
            {isRTL ? 'שגיאה בעיבוד הסרטון' : 'Video Processing Error'}
          </h3>
          {onDismiss && (
            <button 
              className="error-display__dismiss"
              onClick={onDismiss}
              aria-label={isRTL ? 'סגור' : 'Dismiss'}
            >
              <X size={20} />
            </button>
          )}
        </div>

        <div className="error-display__content">
          <p className="error-display__message">{errorMessage}</p>

          {correlationId && (
            <div className="error-display__error-id">
              <span className="error-display__error-id-label">
                {isRTL ? 'מזהה שגיאה:' : 'Error ID:'}
              </span>
              <code className="error-display__error-id-value">{correlationId}</code>
              <button
                className="error-display__copy-button"
                onClick={copyErrorId}
                aria-label={isRTL ? 'העתק מזהה שגיאה' : 'Copy Error ID'}
                title={isRTL ? 'העתק מזהה שגיאה' : 'Copy Error ID'}
              >
                <Copy size={16} />
                {copied && (
                  <span className="error-display__copied-badge">
                    {isRTL ? 'הועתק!' : 'Copied!'}
                  </span>
                )}
              </button>
            </div>
          )}

          {fileName && (
            <div className="error-display__details">
              <div className="error-display__detail-item">
                <span className="error-display__detail-label">
                  {isRTL ? 'שם קובץ:' : 'File Name:'}
                </span>
                <span className="error-display__detail-value">{fileName}</span>
              </div>
              {uploadedOn && (
                <div className="error-display__detail-item">
                  <span className="error-display__detail-label">
                    {isRTL ? 'הועלה ב:' : 'Uploaded On:'}
                  </span>
                  <span className="error-display__detail-value">
                    {new Date(uploadedOn).toLocaleString(i18n.language)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="error-display__actions">
          {retryable && onRetry && (
            <button
              className="error-display__retry-button"
              onClick={onRetry}
            >
              <RefreshCw size={18} />
              {isRTL ? 'נסה שוב' : 'Retry Analysis'}
            </button>
          )}
          {!retryable && (
            <p className="error-display__no-retry-message">
              {isRTL 
                ? 'שגיאה זו לא ניתנת לפתרון על ידי ניסיון חוזר. אנא בדוק את הקובץ ונסה להעלות סרטון אחר.'
                : 'This error cannot be resolved by retrying. Please check your file and try uploading a different video.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorDisplay;







